import { useState, useEffect, useMemo, useRef, useCallback, type Dispatch, type SetStateAction } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgUsers } from '@/hooks/useOrgUsers';
import { mapDocs } from '@/lib/firestoreHelpers';
import { getJobFnol } from '@/utils/jobFnol';
import { formatPhone } from '@/utils/formatPhone';
import { type Office, type Department } from '@/types/org';
import { type JobAssignments, type Job, type ClassificationSource } from '@/types/jobs';
import { type ClassificationReasoningChanges } from '@/utils/stripLossClassificationReasoning';

interface UseJobCreateFormStateParams {
    initialData?: Job;
}

interface PendingClassificationChange {
    field: 'category' | 'class';
    value: number;
}

export const useJobCreateFormState = ({ initialData }: UseJobCreateFormStateParams) => {
    const { profile } = useAuth();
    const { users: orgUsers } = useOrgUsers();
    const [offices, setOffices] = useState<Office[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);

    const [officeId, setOfficeId] = useState('');
    const [departmentId, setDepartmentId] = useState('');

    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');

    const [lossDate, setLossDate] = useState('');
    const [fnolReceivedDate, setFnolReceivedDate] = useState(() => {
        if (initialData) return '';
        return new Date().toISOString().split('T')[0];
    });
    const [lossCategory, setLossCategory] = useState('');
    const [carrier, setCarrier] = useState('');
    const [claimNumber, setClaimNumber] = useState('');
    const [policyNumber, setPolicyNumber] = useState('');
    const [adjusterName, setAdjusterName] = useState('');
    const [adjusterEmail, setAdjusterEmail] = useState('');
    const [adjusterPhone, setAdjusterPhone] = useState('');
    const [lossDescription, setLossDescription] = useState('');
    const [notes, setNotes] = useState('');

    const [address, setAddress] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zip, setZip] = useState('');
    const [county, setCounty] = useState('');

    // IICRC Classification
    const [iicrcCategory, setIicrcCategory] = useState(0);
    const [iicrcClass, setIicrcClass] = useState(0);
    const [classificationManuallyEdited, setClassificationManuallyEdited] = useState(false);
    // The stored classification's provenance on an edit, carried forward
    // unchanged when the person does not change the category or class.
    const [storedClassificationSource, setStoredClassificationSource] = useState<ClassificationSource | undefined>(undefined);
    const [classificationReasoningChanges, setClassificationReasoningChanges] = useState<ClassificationReasoningChanges>({
        category: false,
        class: false,
    });
    const [pendingClassificationChange, setPendingClassificationChange] = useState<PendingClassificationChange | null>(null);

    const [assignments, setAssignments] = useState<JobAssignments>({
        supervisorId: '',
        leadTechnicianId: '',
        teamMemberIds: []
    });

    // ------------------------------------------------------------------
    // Dirty tracking (audit H10a / PR-5): dismissing this form discards a
    // 20-plus-field FNOL entry, so JobCreate needs to know whether the user
    // has actually entered anything. The form's OWN effects also write field
    // state (profile office/department, initialData hydration, the
    // auto-supervisor default) - those are system writes and must NOT count.
    // The split: the hook's effects call the raw useState setters below;
    // consumers only ever receive the wrapped setters from the return value,
    // which flag dirty. Any future internal effect must keep using the raw
    // setters or it will false-flag the form as user-edited.
    // ------------------------------------------------------------------
    const dirtyRef = useRef(false);
    const isDirty = useCallback(() => dirtyRef.current, []);
    const markDirty = useCallback(<T,>(setter: Dispatch<SetStateAction<T>>) => {
        return (value: SetStateAction<T>) => {
            dirtyRef.current = true;
            setter(value);
        };
    }, []);

    useEffect(() => {
        if (!profile?.orgId) return;

        const qOffices = query(collection(db, 'organizations', profile.orgId, 'offices'), where('orgId', '==', profile.orgId));
        const unsubOffices = onSnapshot(qOffices, (snap) => {
            setOffices(mapDocs<Office>(snap));
        });

        return () => unsubOffices();
    }, [profile?.orgId]);

    useEffect(() => {
        if (!officeId || !profile?.orgId) {
            setDepartments([]);
            return;
        }
        const qDepts = query(collection(db, 'organizations', profile.orgId, 'departments'), where('officeId', '==', officeId));
        const unsubDepts = onSnapshot(qDepts, (snap) => {
            setDepartments(mapDocs<Department>(snap));
        });
        return () => unsubDepts();
    }, [officeId, profile?.orgId]);

    useEffect(() => {
        if (profile?.officeId) setOfficeId(profile.officeId);
        if (profile?.departmentId) setDepartmentId(profile.departmentId);
    }, [profile]);

    useEffect(() => {
        if (departmentId && orgUsers.length > 0) {
            // Only auto-set if not already set (or creation mode)
            if (!assignments.supervisorId && !initialData) {
                const supervisor = orgUsers.find(u => u.departmentId === departmentId && u.role === 'DEPT_MANAGER');
                if (supervisor) {
                    setAssignments(prev => ({ ...prev, supervisorId: supervisor.uid }));
                }
            }
        }
    }, [departmentId, orgUsers, assignments.supervisorId, initialData]);

    useEffect(() => {
        if (initialData) {
            setOfficeId(initialData.officeId);
            setDepartmentId(initialData.departmentId);

            const fnol = getJobFnol(initialData);

            setCustomerName(fnol.customer?.name || '');
            setCustomerPhone(fnol.customer?.phone || '');

            setAddress(fnol.property?.address || '');
            setCity(fnol.property?.city || '');
            setState(fnol.property?.state || '');
            setZip(fnol.property?.zip || '');
            setCounty(fnol.property?.county || '');

            setCarrier(fnol.insurance?.carrier || '');
            setClaimNumber(fnol.insurance?.claimNumber || '');
            setPolicyNumber(fnol.insurance?.policyNumber || '');
            setAdjusterName(fnol.insurance?.adjuster?.name || '');
            setAdjusterEmail(fnol.insurance?.adjuster?.email || '');
            setAdjusterPhone(formatPhone(fnol.insurance?.adjuster?.phone || ''));

            setLossCategory(fnol.lossCategory || '');
            setLossDescription(fnol.lossDescription || '');
            setNotes(fnol.notes || '');

            const lDate = fnol.lossDate || null;
            if (lDate) {
                setLossDate(new Date(lDate.seconds * 1000).toISOString().split('T')[0]);
            }

            const fDate = fnol.receivedDate || null;
            if (fDate) {
                setFnolReceivedDate(new Date(fDate.seconds * 1000).toISOString().split('T')[0]);
            }

            if (initialData.assignments) {
                setAssignments(initialData.assignments);
            }

            const activePhase = initialData.phases?.find(p => p.status === 'ACTIVE')
                ?? initialData.phases?.[initialData.phases.length - 1];
            const phaseClassification = (activePhase?.data as unknown as Record<string, unknown>)?.classification as Record<string, unknown> | undefined;
            const rootClassification = (initialData.claimData as unknown as Record<string, unknown>)?.classification as Record<string, unknown> | undefined;
            const existingClassification = phaseClassification ?? rootClassification;
            if (existingClassification) {
                setIicrcCategory(Number(existingClassification.category) || 0);
                setIicrcClass(Number(existingClassification.class) || 0);
            }
            const existingSource = existingClassification?.source;
            setStoredClassificationSource(
                existingSource === 'human' || existingSource === 'ai' ? existingSource : undefined,
            );
            setClassificationManuallyEdited(false);
            setClassificationReasoningChanges({ category: false, class: false });
            setPendingClassificationChange(null);
        }
    }, [initialData]);

    // Helper: Filter users by office (optional, strict) or just show all
    const availableUsers = officeId
        ? orgUsers.filter(u => !u.officeId || u.officeId === officeId) // Include unassigned or matching
        : orgUsers;

    // Consumers get dirty-flagging setters for every user-editable FIELD.
    // useState setters are referentially stable, so wrapping once keeps the
    // returned setters stable too. Deliberately NOT wrapped:
    // setPendingClassificationChange / setClassificationManuallyEdited /
    // setClassificationReasoningChanges - opening or canceling the
    // classification confirm is not a field edit (a CONFIRMED change flags
    // dirty through the wrapped setIicrcCategory/setIicrcClass).
    const fieldSetters = useMemo(() => ({
        setOfficeId: markDirty(setOfficeId),
        setDepartmentId: markDirty(setDepartmentId),
        setCustomerName: markDirty(setCustomerName),
        setCustomerPhone: markDirty(setCustomerPhone),
        setLossDate: markDirty(setLossDate),
        setFnolReceivedDate: markDirty(setFnolReceivedDate),
        setLossCategory: markDirty(setLossCategory),
        setCarrier: markDirty(setCarrier),
        setClaimNumber: markDirty(setClaimNumber),
        setPolicyNumber: markDirty(setPolicyNumber),
        setAdjusterName: markDirty(setAdjusterName),
        setAdjusterEmail: markDirty(setAdjusterEmail),
        setAdjusterPhone: markDirty(setAdjusterPhone),
        setLossDescription: markDirty(setLossDescription),
        setNotes: markDirty(setNotes),
        setAddress: markDirty(setAddress),
        setCity: markDirty(setCity),
        setState: markDirty(setState),
        setZip: markDirty(setZip),
        setCounty: markDirty(setCounty),
        setIicrcCategory: markDirty(setIicrcCategory),
        setIicrcClass: markDirty(setIicrcClass),
        setAssignments: markDirty(setAssignments),
    }), [markDirty]);

    return {
        profile,
        orgUsers,
        offices,
        departments,
        availableUsers,
        isDirty,
        officeId,
        departmentId,
        customerName,
        customerPhone,
        lossDate,
        fnolReceivedDate,
        lossCategory,
        carrier,
        claimNumber,
        policyNumber,
        adjusterName,
        adjusterEmail,
        adjusterPhone,
        lossDescription,
        notes,
        address,
        city,
        state,
        zip,
        county,
        iicrcCategory,
        iicrcClass,
        classificationManuallyEdited, setClassificationManuallyEdited,
        storedClassificationSource,
        classificationReasoningChanges, setClassificationReasoningChanges,
        pendingClassificationChange, setPendingClassificationChange,
        assignments,
        ...fieldSetters,
    };
};

export type JobCreateFormState = ReturnType<typeof useJobCreateFormState>;
